import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Link } from 'react-router-dom';
import { resolveDocsMarkdownHref } from './docsNav';

type Props = {
  content: string;
  /** Current docs path ("" for intro) - used to resolve relative .md links */
  currentPath: string;
};

const DocsMarkdown: React.FC<Props> = ({ content, currentPath }) => {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        h1: ({ children }) => (
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-gray-900 mb-4 mt-0">
            {children}
          </h1>
        ),
        h2: ({ children, id }) => (
          <h2
            id={typeof children === 'string' ? slugify(children) : id}
            className="text-xl sm:text-2xl font-semibold text-gray-900 mt-10 mb-3 pb-2 border-b border-gray-200"
          >
            {children}
          </h2>
        ),
        h3: ({ children }) => (
          <h3 className="text-lg font-semibold text-gray-900 mt-8 mb-2">{children}</h3>
        ),
        p: ({ children }) => (
          <p className="text-[15px] leading-7 text-gray-700 mb-4">{children}</p>
        ),
        ul: ({ children }) => (
          <ul className="list-disc pl-6 mb-4 space-y-1.5 text-[15px] text-gray-700">{children}</ul>
        ),
        ol: ({ children }) => (
          <ol className="list-decimal pl-6 mb-4 space-y-1.5 text-[15px] text-gray-700">{children}</ol>
        ),
        li: ({ children }) => <li className="leading-7">{children}</li>,
        strong: ({ children }) => (
          <strong className="font-semibold text-gray-900">{children}</strong>
        ),
        blockquote: ({ children }) => (
          <blockquote className="border-l-4 border-[#0065F4] bg-[#F1F5FF] px-4 py-3 my-5 rounded-r-lg text-[15px] text-gray-800 [&>p:last-child]:mb-0">
            {children}
          </blockquote>
        ),
        hr: () => <hr className="my-8 border-gray-200" />,
        a: ({ href = '', children }) => {
          if (href.startsWith('#')) {
            return (
              <a href={href} className="text-[#0065F4] hover:underline font-medium">
                {children}
              </a>
            );
          }
          if (href.startsWith('http://') || href.startsWith('https://') || href.startsWith('mailto:')) {
            return (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#0065F4] hover:underline font-medium"
              >
                {children}
              </a>
            );
          }
          const resolved = resolveDocsMarkdownHref(href, currentPath);
          if (resolved && resolved.startsWith('/')) {
            return (
              <Link to={resolved} className="text-[#0065F4] hover:underline font-medium">
                {children}
              </Link>
            );
          }
          return (
            <a href={href} className="text-[#0065F4] hover:underline font-medium">
              {children}
            </a>
          );
        },
        table: ({ children }) => (
          <div className="my-6 overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full text-sm text-left">{children}</table>
          </div>
        ),
        thead: ({ children }) => <thead className="bg-gray-50">{children}</thead>,
        tbody: ({ children }) => <tbody className="divide-y divide-gray-100">{children}</tbody>,
        tr: ({ children }) => <tr className="border-b border-gray-100 last:border-0">{children}</tr>,
        th: ({ children }) => (
          <th className="px-3 py-2.5 font-semibold text-gray-900 whitespace-nowrap">{children}</th>
        ),
        td: ({ children }) => (
          <td className="px-3 py-2.5 text-gray-700 align-top">{children}</td>
        ),
        code: ({ className, children }) => {
          const isBlock = Boolean(className);
          if (isBlock) {
            return (
              <code className="block text-[13px] font-mono text-gray-800 whitespace-pre overflow-x-auto">
                {children}
              </code>
            );
          }
          return (
            <code className="px-1.5 py-0.5 rounded bg-gray-100 text-[13px] font-mono text-gray-800">
              {children}
            </code>
          );
        },
        pre: ({ children }) => (
          <pre className="my-5 p-4 rounded-lg bg-gray-50 border border-gray-200 overflow-x-auto">
            {children}
          </pre>
        ),
      }}
    >
      {content}
    </ReactMarkdown>
  );
};

function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

export default DocsMarkdown;
